import type { Dispatch, FormEvent, SetStateAction } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Card,
  Checkbox,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Paragraph,
  Tabs,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';

import type { Lease, Person } from '../../../shared/models/index';
import * as constants from './constants';
import FaqAccordion from '../Widgets/FaqAccordion';
import { FULLSTORY_EXCLUDE } from '../../../shared/constants/analytics';
import { TabIds } from '../../models/Types';
import LeaseAttachedCheckbox from '../Widgets/LeaseAttachedCheckbox';

type Props = {
  isInSignOnlineMode: boolean;
  lease: Lease;
  state?: string;
  acceptedDisclaimerBefore: boolean;
  isSelfUpload: boolean;
  onOpenEmailPreview(): void;
  onSigningTabSelected(id: TabIds): void;
  onPersonalNoteChange(event: FormEvent<HTMLTextAreaElement>): void;
  personalNote: string;
  setPersonalNote(note: string): void;
  acceptedDisclaimerChecked: boolean;
  setAcceptedDisclaimerChecked: Dispatch<SetStateAction<boolean>>;
  acceptedLeaseAttachmentChecked: boolean;
  setAcceptedLeaseAttachmentChecked: Dispatch<SetStateAction<boolean>>;
  acceptedAttachedLeaseBefore: boolean;
};

const MINIMUM_TENANTS_SHOWN = 2;

export default function ShareLeaseContent({
  isSelfUpload,
  isInSignOnlineMode,
  personalNote,
  setPersonalNote,
  lease,
  acceptedDisclaimerBefore,
  state,
  onPersonalNoteChange,
  onOpenEmailPreview,
  onSigningTabSelected,
  acceptedDisclaimerChecked,
  setAcceptedDisclaimerChecked,
  acceptedLeaseAttachmentChecked,
  setAcceptedLeaseAttachmentChecked,
  acceptedAttachedLeaseBefore,
}: Props): JSX.Element {
  const [tenantsExpanded, setTenantsExpanded] = useState(false);

  const didMountRef = useRef(false);

  useEffect(() => {
    const newPersonalNote = isSelfUpload
      ? constants.SELF_UPLOAD.SHARE_LEASE_CONTENT.DEFAULT_PERSONAL_NOTE
      : isInSignOnlineMode
        ? constants.SIGN_ONLINE.SHARE_LEASE_CONTENT.DEFAULT_PERSONAL_NOTE
        : constants.PRINT_TO_SIGN.SHARE_LEASE_CONTENT.DEFAULT_PERSONAL_NOTE;

    if (personalNote === null) {
      setPersonalNote(newPersonalNote);
    }
  }, []);

  useEffect(() => {
    if (didMountRef.current) {
      const content = isInSignOnlineMode
        ? constants.SIGN_ONLINE.SHARE_LEASE_CONTENT
        : constants.PRINT_TO_SIGN.SHARE_LEASE_CONTENT;

      setPersonalNote(content.DEFAULT_PERSONAL_NOTE);
    } else {
      didMountRef.current = true;
    }
  }, [isInSignOnlineMode]);

  function onTenantsExpandedToggle(): void {
    setTenantsExpanded(!tenantsExpanded);
  }

  function onToggleDisclaimerAccepted() {
    setAcceptedDisclaimerChecked(!acceptedDisclaimerChecked);
  }

  function onToggleAttachedLeaseAccepted() {
    setAcceptedLeaseAttachmentChecked(!acceptedLeaseAttachmentChecked);
  }

  function renderTenants(): React.ReactNode {
    if (lease == null || lease.tenants.length === 0) {
      return null;
    }

    const tenantsToShow = tenantsExpanded ? lease.tenants : lease.tenants.slice(0, MINIMUM_TENANTS_SHOWN);

    return (
      <Card className="tenants-list" tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
        {tenantsToShow.map((tenant: Person, index: number) => (
          <Box
            key={tenant.email}
            css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter', marginBottom: 'tight' }}
          >
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
              <Text css={{ textStyle: 'body-sm', fontWeight: 'bold' }}>Tenant #{index + 1}</Text>
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
              <Text className={FULLSTORY_EXCLUDE} css={{ wordBreak: 'break-all' }}>
                {tenant.getFullName()}
              </Text>
              <Paragraph className={`tenant-email ${FULLSTORY_EXCLUDE}`} css={{ wordBreak: 'break-all' }}>
                {tenant.email}
              </Paragraph>
            </Box>
          </Box>
        ))}
        {lease.tenants.length > MINIMUM_TENANTS_SHOWN && (
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <TextButton tone="brand" onClick={onTenantsExpandedToggle}>
              {tenantsToShow.length < lease.tenants.length ? 'See more' : 'See less'}
            </TextButton>
          </Box>
        )}
        <Button tone="brand" emphasis="outlined" fluid onClick={onOpenEmailPreview}>
          Preview your email
        </Button>
      </Card>
    );
  }

  function renderDisclaimerSubText(): React.ReactNode {
    return isSelfUpload
      ? 'If you have any questions about the legal rights, obligations, or implications of your lease, please consult with an attorney'
      : 'If you have any questions about the legal rights, obligations, enforceability, or implications of this lease, please consult with an attorney';
  }

  function renderTabs(): React.ReactNode {
    return (
      <>
        {isSelfUpload ? (
          <Tabs.Root defaultSelected={TabIds.ELECTRONIC_SIGNING}>
            <Tabs.List>
              <Tabs.Tab value={TabIds.ELECTRONIC_SIGNING}>Electronic signing</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value={TabIds.ELECTRONIC_SIGNING}>
              {renderTabPanelContent(TabIds.ELECTRONIC_SIGNING)}
            </Tabs.Panel>
          </Tabs.Root>
        ) : (
          <Tabs.Root
            selected={isInSignOnlineMode ? TabIds.ONLINE_SIGNING : TabIds.PRINT_TO_SIGN}
            onSelectedChange={onSigningTabSelected}
          >
            <Tabs.List>
              <Tabs.Tab value={TabIds.ONLINE_SIGNING}>Sign online</Tabs.Tab>
              <Tabs.Tab value={TabIds.PRINT_TO_SIGN}>Print to sign</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value={TabIds.ONLINE_SIGNING}>{renderTabPanelContent(TabIds.ONLINE_SIGNING)}</Tabs.Panel>
            <Tabs.Panel value={TabIds.PRINT_TO_SIGN}>{renderTabPanelContent(TabIds.PRINT_TO_SIGN)}</Tabs.Panel>
          </Tabs.Root>
        )}
      </>
    );
  }

  function renderTabPanelContent(tabId: TabIds): React.ReactNode {
    return (
      <>
        <Paragraph css={{ marginBottom: 'tighter' }}>{content.DESCRIPTION_TEXT}</Paragraph>
        {renderTenants()}
        <FormField
          css={{ marginBottom: 'tight' }}
          label={<Label required>Add a personal note</Label>}
          control={
            <Textarea
              rows={6}
              value={personalNote || ''}
              onChange={onPersonalNoteChange}
              css={{ resize: 'vertical' }}
            />
          }
        />
        <FaqAccordion
          initialExpandedIndex={isSelfUpload ? null : 0}
          title="You may be wondering…"
          sections={content.FAQ}
        />
        {(!acceptedDisclaimerBefore ||
          ((tabId === TabIds.ONLINE_SIGNING || tabId === TabIds.ELECTRONIC_SIGNING) &&
            !acceptedAttachedLeaseBefore)) && (
          <Box
            css={{
              background: 'bg.accent.blue.soft',
              padding: 'loose',
              borderRadius: 'tight',
              marginTop: 'tight',
            }}
          >
            {(tabId === TabIds.ONLINE_SIGNING || tabId === TabIds.ELECTRONIC_SIGNING) &&
              !acceptedAttachedLeaseBefore && (
                <LeaseAttachedCheckbox
                  checked={acceptedLeaseAttachmentChecked}
                  onClick={onToggleAttachedLeaseAccepted}
                />
              )}
            {!acceptedDisclaimerBefore && (
              <FormField
                css={{ marginTop: 'tight' }}
                required
                label={<Label>I agree to these terms</Label>}
                control={
                  <LabeledControl
                    label={<Label>{landlordDisclaimer}</Label>}
                    control={<Checkbox checked={acceptedDisclaimerChecked} onClick={onToggleDisclaimerAccepted} />}
                  />
                }
                description={<FormHelp>{renderDisclaimerSubText()}</FormHelp>}
              />
            )}
          </Box>
        )}
      </>
    );
  }

  const content = isSelfUpload
    ? constants.SELF_UPLOAD.SHARE_LEASE_CONTENT
    : isInSignOnlineMode
      ? constants.SIGN_ONLINE.SHARE_LEASE_CONTENT
      : constants.PRINT_TO_SIGN.SHARE_LEASE_CONTENT;

  const landlordDisclaimer = isSelfUpload
    ? constants.SELF_UPLOAD_UPLOAD_DISCLAIMER
    : constants.getZillowLeasesDisclaimer(state);

  return <>{renderTabs()}</>;
}
